/**
 * Subset minimalista de markdown para descripciones de eventos.
 *
 * Soporta solo dos marcas: `**bold**` y `_italic_`. La elección es a
 * propósito — el editor del dashboard expone solo esos dos botones, así que
 * el contrato es predictible y no necesitamos meter una dep de ~30 KB
 * (react-markdown / marked) en el bundle público.
 *
 * Seguridad: SIEMPRE escapamos HTML antes de aplicar las regex de marca,
 * y solo emitimos `<strong>` y `<em>` desde acá. El consumer puede pasar
 * el output a `dangerouslySetInnerHTML` con confianza porque el universo
 * de tags posibles es ese par fijo.
 */

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function escapeHtml(input: string): string {
  return input.replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch] ?? ch);
}

/**
 * Devuelve HTML seguro con `<strong>` y `<em>` aplicados.
 *
 * Regex notes:
 *   - `**` (bold) se evalúa primero para que `**_x_**` resulte en
 *     `<strong><em>x</em></strong>` (anidación correcta).
 *   - Las marcas NO matchean a través de saltos de línea (`[^*\n]+`) para
 *     no atrapar pares accidentales separados por párrafos.
 *   - El cuerpo debe ser no-vacío (`+`) y no contener la misma marca
 *     dentro para evitar overlapping ambiguo.
 */
export function renderInlineMarks(input: string): string {
  const escaped = escapeHtml(input);
  const withBold = escaped.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  const withItalic = withBold.replace(/(^|[\s>])_([^_\n]+)_(?=$|[\s.,;:!?<])/g, '$1<em>$2</em>');
  return withItalic;
}

/**
 * Limpia las marcas para usos donde necesitamos texto plano:
 *   - Meta description / Open Graph snippets.
 *   - schema.org Event.description (debe ser plain text).
 *   - Truncados con `.slice()` que romperían el balanceo de marcas.
 */
export function stripInlineMarks(input: string): string {
  return input
    .replace(/\*\*([^*\n]+)\*\*/g, '$1')
    .replace(/(^|[\s>])_([^_\n]+)_(?=$|[\s.,;:!?<])/g, '$1$2');
}
